/**
 * Precipitation conversion utilities.
 * Handles precipitation depth (in/mm) and rate (in/hr ↔ mm/hr).
 * Uses millimeters as the base (SI) unit internally.
 */

import { Units } from '../../units';

const IN_TO_MM = 25.4;
const MM_TO_IN = 1 / IN_TO_MM;

/**
 * Convert precipitation depth to millimeters (base unit).
 */
export function precipToMm(value: number, fromUnit: string): number {
  switch (fromUnit.toLowerCase().trim()) {
    case 'in':
    case 'inches':
      return value * IN_TO_MM;
    case 'mm':
    case 'millimeters':
      return value;
    default:
      return value;
  }
}

/**
 * Convert precipitation depth from mm to the target unit system.
 */
export function precipFromMm(
  valueMm: number,
  targetSystem: Units,
): { value: number; unit: string } {
  switch (targetSystem) {
    case Units.IMPERIAL:
      return { value: valueMm * MM_TO_IN, unit: 'in' };
    case Units.METRIC:
    case Units.MARITIME:
    default:
      return { value: valueMm, unit: 'mm' };
  }
}

/**
 * Convert precipitation rate to mm/hr (base unit).
 */
export function precipRateToMmHr(value: number, fromUnit: string): number {
  switch (fromUnit.toLowerCase().trim()) {
    case 'in/hr':
    case 'in/h':
      return value * IN_TO_MM;
    case 'mm/hr':
    case 'mm/h':
      return value;
    default:
      return value;
  }
}

/**
 * Convert precipitation rate from mm/hr to the target unit system.
 */
export function precipRateFromMmHr(
  valueMmHr: number,
  targetSystem: Units,
): { value: number; unit: string } {
  switch (targetSystem) {
    case Units.IMPERIAL:
      return { value: valueMmHr * MM_TO_IN, unit: 'in/hr' };
    case Units.METRIC:
    case Units.MARITIME:
    default:
      return { value: valueMmHr, unit: 'mm/hr' };
  }
}

/**
 * Convert precipitation (depth or rate) from a source unit to the user's preferred system.
 */
export function convertPrecipitation(
  value: number,
  fromUnit: string,
  targetSystem: Units,
): { value: number; unit: string } {
  const normalized = fromUnit.toLowerCase().trim();

  if (
    normalized === 'in/hr' ||
    normalized === 'in/h' ||
    normalized === 'mm/hr' ||
    normalized === 'mm/h'
  ) {
    const mmHr = precipRateToMmHr(value, fromUnit);
    return precipRateFromMmHr(mmHr, targetSystem);
  }

  const mm = precipToMm(value, fromUnit);
  return precipFromMm(mm, targetSystem);
}
